/*
 * Authoritative network topology data.
 *
 * Every component that references PoPs — /network page, configurator,
 * 2D/3D maps, looking glass, provisioning demo, SC-NIA system prompt —
 * should derive its data from here. Keeping one source of truth is
 * what prevents drift like the BIXP/MegaIX and Vienna incidents.
 *
 * Tiers:
 *   - "international"  : the 9 multi-region carrier PoPs (Sofia, Frankfurt,
 *                        Istanbul, Athens, London, Tbilisi, Dubai, Yerevan, Agarak)
 *   - "domestic"       : the 9 Bulgarian regional Edge PoPs that deliver
 *                        the national backbone (DWDM + Ethernet L2VPN)
 *
 * NOTE on `Pop.services`:
 *   The `services` field on every PoP below is now DERIVED at module load
 *   from `src/data/service-availability.ts` — the single source of truth
 *   for per-service, per-PoP deliverability. Do NOT hand-edit `services`
 *   on individual PoPs; edit `serviceAvailability` in service-availability.ts
 *   and the change flows here automatically. The literal `services: [...]`
 *   arrays kept below are placeholders retained only so this module stays
 *   plain-data-shaped for existing consumers — they are overwritten in the
 *   `applyDerivedServices()` pass at the bottom of this file.
 */

import {
  popsFor,
  type ServiceId,
} from "@/data/service-availability";

export type PopTier = "international" | "domestic";

export interface Pop {
  id: string;
  city: string;
  country: string;
  countryCode: string;
  flag: string;
  lat: number;
  lng: number;
  facility: string;
  tier: PopTier;
  services: string[];
  /** Approximate one-way fiber latency from Sofia core in ms (indicative). */
  latencyMsFromSofia?: number;
  /** PeeringDB facility listing. */
  inPeeringDb?: boolean;
  /** Strategic note — e.g. "Submarine cable landing station". */
  note?: string;
  /** Set when this PoP is a major submarine/terrestrial cable landing point. */
  cableLanding?: {
    system: string;
    type: "submarine" | "terrestrial";
    description: string;
  };
}

/* ─── International — 9 carrier PoPs ─── */
export const INTERNATIONAL_POPS: Pop[] = [
  {
    id: "sofia",
    city: "Sofia",
    country: "Bulgaria",
    countryCode: "BG",
    flag: "🇧🇬",
    lat: 42.6977,
    lng: 23.3219,
    facility: "Equinix SO1 & SO2, Telepoint",
    tier: "international",
    // Overwritten at module load by deriveServicesForPop() from
    // service-availability.ts — kept here only as a placeholder so
    // the Pop type stays satisfied. Do not read the literal.
    services: [],
    latencyMsFromSofia: 0,
    inPeeringDb: true,
    note: "NOC and core routing",
  },
  {
    id: "frankfurt",
    city: "Frankfurt",
    country: "Germany",
    countryCode: "DE",
    flag: "🇩🇪",
    lat: 50.1109,
    lng: 8.6821,
    facility: "Equinix FR5, NewTelco Frankfurt",
    tier: "international",
    services: ["IP Transit", "DWDM", "Ethernet L2VPN"],
    latencyMsFromSofia: 25,
    inPeeringDb: true,
  },
  {
    id: "istanbul",
    city: "Istanbul",
    country: "Türkiye",
    countryCode: "TR",
    flag: "🇹🇷",
    lat: 41.0082,
    lng: 28.9784,
    facility: "Equinix IL2",
    tier: "international",
    services: ["IP Transit", "DWDM", "Ethernet L2VPN"],
    latencyMsFromSofia: 12,
  },
  {
    id: "athens",
    city: "Athens",
    country: "Greece",
    countryCode: "GR",
    flag: "🇬🇷",
    lat: 37.9838,
    lng: 23.7275,
    facility: "Lamda Hellix",
    tier: "international",
    services: ["IP Transit", "DWDM"],
    latencyMsFromSofia: 11,
  },
  {
    id: "london",
    city: "London",
    country: "United Kingdom",
    countryCode: "GB",
    flag: "🇬🇧",
    lat: 51.5074,
    lng: -0.1278,
    facility: "Telehouse North",
    tier: "international",
    services: ["IP Transit", "DWDM"],
    latencyMsFromSofia: 42,
  },
  {
    id: "tbilisi",
    city: "Tbilisi",
    country: "Georgia",
    countryCode: "GE",
    flag: "🇬🇪",
    lat: 41.7151,
    lng: 44.8271,
    facility: "Cloud9 Data Center",
    tier: "international",
    services: ["IP Transit", "DWDM", "Ethernet L2VPN"],
    latencyMsFromSofia: 46,
  },
  {
    id: "dubai",
    city: "Dubai",
    country: "United Arab Emirates",
    countryCode: "AE",
    flag: "🇦🇪",
    lat: 25.2048,
    lng: 55.2708,
    facility: "Datamena DC1",
    tier: "international",
    services: ["IP Transit", "DWDM"],
    latencyMsFromSofia: 78,
  },
  {
    id: "yerevan",
    city: "Yerevan",
    country: "Armenia",
    countryCode: "AM",
    flag: "🇦🇲",
    lat: 40.1792,
    lng: 44.4991,
    facility: "Firebird",
    tier: "international",
    services: ["IP Transit", "DWDM"],
    latencyMsFromSofia: 52,
  },
  {
    id: "agarak",
    city: "Agarak",
    country: "Armenia",
    countryCode: "AM",
    flag: "🇦🇲",
    lat: 38.8461,
    lng: 46.1989,
    facility: "Armenia–Iran Border Landing Station",
    tier: "international",
    // Overwritten at module load by deriveServicesForPop() from
    // service-availability.ts — dark fibre is Sofia-metro-only today,
    // so Agarak only carries DWDM + L2VPN. Placeholder kept for the type.
    services: [],
    latencyMsFromSofia: 58,
    note: "Terrestrial cable landing — Armenia↔Iran overland corridor",
    cableLanding: {
      system: "Armenia–Iran Terrestrial",
      type: "terrestrial",
      description: "Overland fiber corridor into Iran.",
    },
  },
];

/* ─── Domestic — 9 Bulgarian Edge PoPs ─── */
export const DOMESTIC_POPS: Pop[] = [
  {
    id: "plovdiv",
    city: "Plovdiv",
    country: "Bulgaria",
    countryCode: "BG",
    flag: "🇧🇬",
    lat: 42.1354,
    lng: 24.7453,
    facility: "Sofia Connect Edge",
    tier: "domestic",
    services: ["IP Transit", "DWDM"],
    latencyMsFromSofia: 2,
  },
  {
    id: "stara-zagora",
    city: "Stara Zagora",
    country: "Bulgaria",
    countryCode: "BG",
    flag: "🇧🇬",
    lat: 42.4258,
    lng: 25.6345,
    facility: "Sofia Connect Edge",
    tier: "domestic",
    services: ["IP Transit", "DWDM"],
    latencyMsFromSofia: 3,
  },
  {
    id: "burgas",
    city: "Burgas",
    country: "Bulgaria",
    countryCode: "BG",
    flag: "🇧🇬",
    lat: 42.5048,
    lng: 27.4626,
    facility: "Sofia Connect Edge",
    tier: "domestic",
    services: ["IP Transit", "DWDM"],
    latencyMsFromSofia: 5,
  },
  {
    id: "varna",
    city: "Varna",
    country: "Bulgaria",
    countryCode: "BG",
    flag: "🇧🇬",
    lat: 43.2141,
    lng: 27.9147,
    facility: "Sofia Connect Edge",
    tier: "domestic",
    services: ["IP Transit", "DWDM"],
    latencyMsFromSofia: 5,
  },
  {
    id: "balchik",
    city: "Balchik",
    country: "Bulgaria",
    countryCode: "BG",
    flag: "🇧🇬",
    lat: 43.4212,
    lng: 28.1669,
    facility: "Sofia Connect Edge — Caucasus Cable Landing Station",
    tier: "domestic",
    // Overwritten at module load by deriveServicesForPop() from
    // service-availability.ts — dark fibre is Sofia-metro-only today,
    // so Balchik only carries DWDM + L2VPN. Placeholder kept for the type.
    services: [],
    latencyMsFromSofia: 6,
    note: "Submarine cable landing — Caucasus Cable System (Bulgaria ↔ Georgia across the Black Sea)",
    cableLanding: {
      system: "Caucasus Cable System",
      type: "submarine",
      description:
        "Bulgaria ↔ Georgia submarine fiber across the Black Sea, linking the EU backbone directly to the Caucasus corridor without transiting Türkiye.",
    },
  },
  {
    id: "dobrich",
    city: "Dobrich",
    country: "Bulgaria",
    countryCode: "BG",
    flag: "🇧🇬",
    lat: 43.5727,
    lng: 27.8275,
    facility: "Sofia Connect Edge",
    tier: "domestic",
    services: ["IP Transit", "DWDM"],
    latencyMsFromSofia: 6,
  },
  {
    id: "montana",
    city: "Montana",
    country: "Bulgaria",
    countryCode: "BG",
    flag: "🇧🇬",
    lat: 43.4096,
    lng: 23.2257,
    facility: "Sofia Connect Edge",
    tier: "domestic",
    services: ["IP Transit", "DWDM"],
    latencyMsFromSofia: 2,
  },
  {
    id: "pleven",
    city: "Pleven",
    country: "Bulgaria",
    countryCode: "BG",
    flag: "🇧🇬",
    lat: 43.4170,
    lng: 24.6067,
    facility: "Sofia Connect Edge",
    tier: "domestic",
    services: ["IP Transit", "DWDM"],
    latencyMsFromSofia: 3,
  },
  {
    id: "blagoevgrad",
    city: "Blagoevgrad",
    country: "Bulgaria",
    countryCode: "BG",
    flag: "🇧🇬",
    lat: 42.0119,
    lng: 23.0991,
    facility: "Sofia Connect Edge",
    tier: "domestic",
    services: ["IP Transit", "DWDM"],
    latencyMsFromSofia: 3,
  },
];

/* ─── Derived services ───
 *
 * Rebuild every Pop.services array from service-availability.ts so this
 * module can never drift from ground truth. Display order is stable:
 * IP Transit → Ethernet L2VPN → DWDM → Dark Fibre — matches how the
 * services are listed on marketing pages.
 */
const SERVICE_DISPLAY: readonly { id: ServiceId; label: string }[] = [
  { id: "ip-transit", label: "IP Transit" },
  { id: "ethernet-l2vpn", label: "Ethernet L2VPN" },
  { id: "dwdm", label: "DWDM" },
  { id: "dark-fibre", label: "Dark Fibre" },
];

function deriveServicesForPop(popId: string): string[] {
  const out: string[] = [];
  for (const { id, label } of SERVICE_DISPLAY) {
    if ((popsFor(id) as readonly string[]).includes(popId)) out.push(label);
  }
  return out;
}

for (const pop of INTERNATIONAL_POPS) {
  pop.services = deriveServicesForPop(pop.id);
}
for (const pop of DOMESTIC_POPS) {
  pop.services = deriveServicesForPop(pop.id);
}

/* ─── Union + helpers ─── */
export const ALL_POPS: Pop[] = [...INTERNATIONAL_POPS, ...DOMESTIC_POPS];

export const POP_COUNT = {
  international: INTERNATIONAL_POPS.length,
  domestic: DOMESTIC_POPS.length,
  total: ALL_POPS.length,
};

/** Cities where the agent has verified operational presence — for SC-NIA guard. */
export const OPERATIONAL_CITIES: string[] = ALL_POPS.map((p) => p.city);

/** Known non-operational cities we are sometimes asked about. */
export const NON_OPERATIONAL_CITIES: string[] = [
  "Vienna",
  "Warsaw",
  "Prague",
  "Bucharest",
  "Belgrade",
  "Zagreb",
  "Skopje",
  "Tirana",
];

export function findPopById(id: string): Pop | undefined {
  return ALL_POPS.find((p) => p.id === id);
}

export function findPopByCity(city: string): Pop | undefined {
  const needle = city.trim().toLowerCase();
  return ALL_POPS.find((p) => p.city.toLowerCase() === needle);
}
